'use client';

import { getAspectRatioOrientation } from '@repo/artwork-generation/print-ratio-catalog';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { CompatiblePrintSizes } from './CompatiblePrintSizes';
import {
  aspectRatioOptions,
  getDefaultAspectRatioSelection,
  getAspectRatioOption,
  getAspectRatioSelectionUpdates,
} from '@/components/creation-workspace/placeholder-data';
import {
  AspectRatioOrientationTabs,
  type AspectRatioOrientation,
} from '@/components/creation-workspace/AspectRatioOrientationTabs';

type PreviewSize = { width: number; height: number };

interface AspectRatioSliderSelectorProps {
  aspectRatio: string | null;
  printSize: string | null;
  onChange: (selection: { aspectRatio: string | null; printSize: string | null }) => void;
  compact?: boolean;
  showGuidance?: boolean;
  showSelectedDetails?: boolean;
  showSizePills?: boolean;
  lastSelectedAspectRatio?: string | null;
  showLastSelectedBadge?: boolean;
  allowAll?: boolean;
  showAllOption?: boolean;
  stackTabsOnMobile?: boolean;
  allAriaLabel?: string;
  allLabel?: string;
  allDescription?: string;
}

const ORIENTATION_DEFAULT_IDS: Record<AspectRatioOrientation, string> = {
  portrait: '2:3',
  square: '1:1',
  landscape: '3:2',
};

const sliderAspectRatioOptions = [...aspectRatioOptions].sort(
  (left, right) =>
    left.preview.width / left.preview.height - right.preview.width / right.preview.height,
);

const DEFAULT_SQUARE_INDEX = Math.max(
  sliderAspectRatioOptions.findIndex(option => option.id === ORIENTATION_DEFAULT_IDS.square),
  0,
);
const DEFAULT_SLIDER_OPTION = sliderAspectRatioOptions[DEFAULT_SQUARE_INDEX]!;

const getOrientationGroupOptions = (orientation: AspectRatioOrientation) =>
  sliderAspectRatioOptions.filter(option => getAspectRatioOrientation(option.id) === orientation);

const getDefaultOrientationOption = (orientation: AspectRatioOrientation) => {
  const preferred = sliderAspectRatioOptions.find(
    option => option.id === ORIENTATION_DEFAULT_IDS[orientation],
  );

  if (preferred) {
    return preferred;
  }

  const groupedOptions = getOrientationGroupOptions(orientation);
  return groupedOptions[Math.floor(groupedOptions.length / 2)] ?? DEFAULT_SLIDER_OPTION;
};

const scalePreviewSize = (
  preview: PreviewSize,
  largestDimension: number,
  minimumDimension: number,
): PreviewSize => {
  const scale = largestDimension / Math.max(preview.width, preview.height);

  return {
    width: Math.max(Math.round(preview.width * scale), minimumDimension),
    height: Math.max(Math.round(preview.height * scale), minimumDimension),
  };
};

const getCanvasCornerRadius = (
  preview: PreviewSize,
  maxRadius: number,
  minRadius: number,
): number => {
  const smallestDimension = Math.min(preview.width, preview.height);

  return Math.max(minRadius, Math.min(Math.round(smallestDimension * 0.16), maxRadius));
};

export const AspectRatioSliderSelector = ({
  aspectRatio,
  printSize: _printSize,
  onChange,
  compact = false,
  showGuidance = false,
  showSelectedDetails = true,
  showSizePills = true,
  lastSelectedAspectRatio = null,
  showLastSelectedBadge = false,
  allowAll = false,
  showAllOption = true,
  stackTabsOnMobile = false,
  allAriaLabel,
  allLabel,
  allDescription,
}: AspectRatioSliderSelectorProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const t = useTranslations('create.sizeAndQualityModal');
  const sharedT = useTranslations('create.methodFlow.settings.shared');
  const isAllSelected = allowAll && aspectRatio === null;

  const defaultSelection = useMemo(
    () => getDefaultAspectRatioSelection(aspectRatio, _printSize),
    [aspectRatio, _printSize],
  );
  const selectedAspectRatio = isAllSelected
    ? null
    : (getAspectRatioOption(aspectRatio) ?? getAspectRatioOption(defaultSelection.aspectRatio));
  const displayAspectRatio = selectedAspectRatio ?? DEFAULT_SLIDER_OPTION;
  const availablePrintSizes = displayAspectRatio.printSizes;
  const currentOrientation = useMemo<AspectRatioOrientation>(() => {
    const orientation = getAspectRatioOrientation(displayAspectRatio?.id);

    if (orientation === 'portrait' || orientation === 'landscape') {
      return orientation;
    }

    return 'square';
  }, [displayAspectRatio?.id]);
  const activeSliderOptions = useMemo(
    () => getOrientationGroupOptions(currentOrientation),
    [currentOrientation],
  );
  const selectedIndex = Math.max(
    activeSliderOptions.findIndex(option => option.id === displayAspectRatio?.id),
    0,
  );
  const thumbPosition =
    activeSliderOptions.length > 1 ? (selectedIndex / (activeSliderOptions.length - 1)) * 100 : 0;
  const hoveredOption = hoveredIndex !== null ? (activeSliderOptions[hoveredIndex] ?? null) : null;
  const hoveredThumbPosition =
    hoveredIndex !== null && activeSliderOptions.length > 1
      ? (hoveredIndex / (activeSliderOptions.length - 1)) * 100
      : 0;
  const previewSize = scalePreviewSize(displayAspectRatio.preview, compact ? 104 : 142, 26);
  const previewFrameRadius = getCanvasCornerRadius(previewSize, compact ? 10 : 12, 4);
  const [widthUnits = '1', heightUnits = '1'] = displayAspectRatio.ratio.split(':');
  const guideRightSpace = compact ? 58 : 74;
  const guideBottomSpace = compact ? 36 : 42;
  const guideOffset = compact ? 18 : 24;

  const selectedAspectRatioName = useMemo(() => {
    if (!displayAspectRatio) {
      return null;
    }

    try {
      return sharedT(`aspectRatioOptions.${displayAspectRatio.id}.name`);
    } catch {
      return displayAspectRatio.name;
    }
  }, [displayAspectRatio, sharedT]);

  const selectedAspectRatioDescription = useMemo(() => {
    if (!displayAspectRatio) {
      return null;
    }

    try {
      return sharedT(`aspectRatioOptions.${displayAspectRatio.id}.description`);
    } catch {
      return displayAspectRatio.description;
    }
  }, [displayAspectRatio, sharedT]);
  const isLastSelectedAspectRatio = Boolean(
    showLastSelectedBadge &&
    lastSelectedAspectRatio &&
    displayAspectRatio.id === lastSelectedAspectRatio,
  );
  const orientationLabel = t(`slider.orientation.${currentOrientation}`);
  const selectedPreviewTitle = selectedAspectRatioName
    ? selectedAspectRatioName.toLowerCase().includes(orientationLabel.toLowerCase())
      ? selectedAspectRatioName
      : `${selectedAspectRatioName} ${orientationLabel}`
    : '';

  useEffect(() => {
    if (isAllSelected) {
      return;
    }

    if (getAspectRatioOption(aspectRatio)) {
      return;
    }

    onChange(defaultSelection);
  }, [aspectRatio, defaultSelection, isAllSelected, onChange]);

  useEffect(() => {
    setHoveredIndex(null);
  }, [currentOrientation]);

  const handleSelectionChange = (nextAspectRatioId: string) => {
    onChange(getAspectRatioSelectionUpdates(nextAspectRatioId, _printSize));
  };

  const handleSliderHover = (clientX: number, element: HTMLDivElement) => {
    const { left, width } = element.getBoundingClientRect();

    if (width <= 0) {
      return;
    }

    const clampedOffset = Math.min(Math.max(clientX - left, 0), width);
    const nextIndex = Math.round((clampedOffset / width) * (activeSliderOptions.length - 1));

    setHoveredIndex(nextIndex);
  };

  if (!displayAspectRatio || !selectedAspectRatioName || !selectedAspectRatioDescription) {
    return null;
  }

  return (
    <div className="space-y-3">
      {showGuidance && (
        <p className="text-sm text-gray-500 dark:text-slate-400">
          {t('selection.aspectRatioHint')}
        </p>
      )}

      <div className="flex justify-center">
        <AspectRatioOrientationTabs
          selectedOrientation={isAllSelected ? null : currentOrientation}
          onOrientationChange={orientation =>
            handleSelectionChange(getDefaultOrientationOption(orientation).id)
          }
          allowAll={allowAll && showAllOption}
          allLabel={allLabel}
          allAriaLabel={allAriaLabel}
          stackOnMobile={stackTabsOnMobile}
          onAllChange={() => onChange({ aspectRatio: null, printSize: null })}
        />
      </div>

      {isAllSelected ? (
        <div
          className={`rounded-[22px] border border-dashed border-gray-200 bg-gray-50/70 text-center dark:border-slate-700/80 dark:bg-slate-800/35 ${
            compact ? 'px-4 py-8' : 'px-6 py-10'
          }`}
        >
          <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
            {allLabel ?? t('slider.all')}
          </p>
          {allDescription && (
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-gray-500 dark:text-slate-400">
              {allDescription}
            </p>
          )}
        </div>
      ) : (
        <div
          className={`rounded-[22px] border border-gray-200 bg-white dark:border-slate-700/80 dark:bg-slate-800/55 ${
            compact ? 'p-3' : 'p-4'
          }`}
        >
          <div className="space-y-4">
            {/* Reserve room for square print sizes and anchor the preview as the list changes. */}
            <div
              className={`flex w-full items-start justify-center overflow-hidden rounded-[18px] border border-gray-200 bg-gray-50/50 dark:border-slate-700/80 dark:bg-slate-900/45 ${
                compact ? 'min-h-[250px] p-4' : 'min-h-[342px] p-5'
              }`}
            >
              <div
                className={`flex w-full max-w-2xl ${
                  compact
                    ? 'flex-col gap-4 text-center'
                    : 'grid grid-cols-[minmax(0,1fr)_minmax(15rem,18rem)] items-start gap-14 text-left'
                }`}
              >
                <div className="flex justify-center">
                  <div
                    className="relative shrink-0"
                    style={{
                      width: previewSize.width + guideRightSpace,
                      height: previewSize.height + guideBottomSpace,
                    }}
                  >
                    <div className="absolute left-0 top-0">
                      <motion.div
                        className="flex items-center justify-center border-2 border-orange-400 bg-orange-50/85 text-orange-700 shadow-[0_0_0_1px_rgba(251,146,60,0.18)] dark:border-orange-400 dark:bg-orange-500/10 dark:text-orange-100 dark:shadow-[0_0_0_1px_rgba(251,146,60,0.18)]"
                        style={{
                          width: previewSize.width,
                          height: previewSize.height,
                          borderRadius: previewFrameRadius,
                        }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                      >
                        <span
                          className={`${compact ? 'text-lg' : 'text-2xl'} font-semibold tracking-normal`}
                        >
                          {displayAspectRatio.ratio}
                        </span>
                      </motion.div>
                    </div>

                    <div
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        left: 0,
                        top: previewSize.height + guideOffset,
                        width: previewSize.width,
                      }}
                    >
                      <div className="relative h-6">
                        <div className="absolute left-0 right-0 top-1 border-t border-slate-300 dark:border-slate-600" />
                        <div className="absolute left-0 top-[-3px] h-2 border-l border-slate-300 dark:border-slate-600" />
                        <div className="absolute right-0 top-[-3px] h-2 border-r border-slate-300 dark:border-slate-600" />
                        <span className="absolute left-1/2 top-3 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-slate-500 dark:text-slate-400">
                          {t('slider.unitsLabel', { value: Number(widthUnits) })}
                        </span>
                      </div>
                    </div>

                    <div
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        left: previewSize.width + guideOffset,
                        top: 0,
                        height: previewSize.height,
                      }}
                    >
                      <div className="relative h-full w-20">
                        <div className="absolute bottom-0 left-1 top-0 border-l border-slate-300 dark:border-slate-600" />
                        <div className="absolute left-[-3px] top-0 w-2 border-t border-slate-300 dark:border-slate-600" />
                        <div className="absolute bottom-0 left-[-3px] w-2 border-b border-slate-300 dark:border-slate-600" />
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap text-xs font-medium text-slate-500 dark:text-slate-400">
                          {t('slider.unitsLabel', { value: Number(heightUnits) })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`${compact ? 'w-full' : 'w-44'} shrink-0`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                    {selectedPreviewTitle}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-gray-400 dark:text-slate-500">
                    {t('slider.shapeLabel', { ratio: displayAspectRatio.ratio })}
                  </p>

                  {displayAspectRatio.displayOnly && !showSelectedDetails && (
                    <div className={compact ? 'mt-3' : 'mt-5'}>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                        {t('slider.displayOnlyTitle')}
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-slate-400">
                        {t('slider.displayOnlyNote')}
                      </p>
                    </div>
                  )}

                  {showSizePills &&
                    !displayAspectRatio.displayOnly &&
                    availablePrintSizes.length > 0 && (
                      <div className={compact ? 'mt-3' : 'mt-5'}>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-slate-400">
                          {t('slider.commonSizes')}
                        </p>
                        <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                          {t('selection.printSizeInfo')}
                        </p>
                        <CompatiblePrintSizes
                          aspectRatio={displayAspectRatio.id}
                          orientation={currentOrientation}
                          printSizes={availablePrintSizes}
                          compact={compact}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>

            {showSelectedDetails && (
              <div className="flex flex-col items-center text-center">
                <div className="space-y-2">
                  <p
                    className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900 dark:text-slate-100`}
                  >
                    {selectedAspectRatioName}
                  </p>
                  <p className="text-[11px] leading-relaxed text-gray-500 dark:text-slate-400">
                    {selectedAspectRatioDescription}
                  </p>
                </div>

                {displayAspectRatio.displayOnly ? (
                  <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-3 py-3 text-center dark:border-amber-400/20 dark:bg-amber-500/10">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-200">
                      {sharedT('printSize.displayOnlyEyebrow')}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-amber-900 dark:text-amber-100">
                      {sharedT('printSize.displayOnlyTitle')}
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-amber-700 dark:text-amber-200/90">
                      {sharedT('printSize.displayOnlyDescription')}
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            <div
              className={`border-t border-gray-200/80 pt-4 dark:border-slate-700/70 ${
                currentOrientation === 'square' ? 'invisible' : ''
              }`}
              aria-hidden={currentOrientation === 'square'}
              inert={currentOrientation === 'square'}
            >
              <div className="mb-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                  {t('slider.title')}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                  {t('slider.description')}
                </p>
              </div>
              <div className="relative px-1 py-1">
                <div
                  className="relative h-12 cursor-pointer"
                  onMouseMove={event => handleSliderHover(event.clientX, event.currentTarget)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="absolute inset-x-3 top-4">
                    <div className="relative h-1">
                      <div className="absolute inset-0 rounded-full bg-gray-200 dark:bg-slate-700/90" />

                      {hoveredOption && (
                        <motion.div
                          className="pointer-events-none absolute z-30 -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-300/60 bg-slate-950/95 px-2.5 py-1 text-[10px] font-semibold tracking-[0.14em] text-amber-100 shadow-[0_12px_24px_rgba(2,6,23,0.42)]"
                          style={{
                            left: `${hoveredThumbPosition}%`,
                            bottom: 'calc(100% + 12px)',
                          }}
                          initial={{ opacity: 0, y: 4, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                        >
                          {hoveredOption.ratio}
                        </motion.div>
                      )}

                      <div className="absolute inset-x-0 top-1/2 z-20 h-8 -translate-y-1/2">
                        {activeSliderOptions.map((option, index) => {
                          const isActive = index === selectedIndex;
                          const isHovered = index === hoveredIndex;
                          const pointPosition =
                            activeSliderOptions.length > 1
                              ? (index / (activeSliderOptions.length - 1)) * 100
                              : 0;

                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => handleSelectionChange(option.id)}
                              onMouseEnter={() => setHoveredIndex(index)}
                              onFocus={() => setHoveredIndex(index)}
                              onBlur={() =>
                                setHoveredIndex(current => (current === index ? null : current))
                              }
                              title={option.ratio}
                              aria-label={`${option.name} ${option.ratio}`}
                              className="absolute top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full cursor-pointer"
                              style={{ left: `${pointPosition}%` }}
                            >
                              <span
                                className={`h-2.5 w-2.5 rounded-full border transition-all ${
                                  isActive
                                    ? 'border-transparent bg-transparent shadow-none'
                                    : isHovered
                                      ? 'border-amber-300 bg-amber-100 shadow-[0_0_0_4px_rgba(251,191,36,0.16)] dark:border-amber-300 dark:bg-amber-300/20 dark:shadow-[0_0_0_4px_rgba(251,191,36,0.12)]'
                                      : 'border-gray-300 bg-gray-100 dark:border-slate-600 dark:bg-slate-900'
                                }`}
                              />
                            </button>
                          );
                        })}
                      </div>

                      <motion.div
                        className="pointer-events-none absolute top-1/2 z-20 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-amber-500 shadow-[0_8px_20px_rgba(245,158,11,0.28)] dark:border-slate-950 dark:bg-amber-400"
                        animate={{ left: `${thumbPosition}%` }}
                        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                      />

                      {isLastSelectedAspectRatio && (
                        <motion.div
                          className="pointer-events-none absolute left-0 z-30 -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700 shadow-[0_8px_16px_rgba(251,191,36,0.12)] dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200"
                          style={{
                            left: `${thumbPosition}%`,
                            top: 'calc(100% + 24px)',
                          }}
                          initial={{ opacity: 0, y: -2, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                        >
                          {t('selection.lastSelected')}
                        </motion.div>
                      )}
                    </div>
                  </div>

                  <div className="absolute inset-x-3 top-7 h-5">
                    {activeSliderOptions.map((option, index) => {
                      const isActive = index === selectedIndex;
                      const pointPosition =
                        activeSliderOptions.length > 1
                          ? (index / (activeSliderOptions.length - 1)) * 100
                          : 0;

                      return (
                        <button
                          key={`${option.id}-label`}
                          type="button"
                          onClick={() => handleSelectionChange(option.id)}
                          className={`absolute top-0 z-20 -translate-x-1/2 rounded px-1 text-xs font-semibold transition-colors ${
                            isActive
                              ? 'text-orange-700 dark:text-orange-200'
                              : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
                          }`}
                          style={{ left: `${pointPosition}%` }}
                        >
                          {option.ratio}
                        </button>
                      );
                    })}
                  </div>

                  <input
                    type="range"
                    min={0}
                    max={activeSliderOptions.length - 1}
                    step={1}
                    value={selectedIndex}
                    onChange={event => {
                      const nextOption = activeSliderOptions[Number(event.target.value)];

                      if (nextOption) {
                        handleSelectionChange(nextOption.id);
                      }
                    }}
                    aria-label={t('labels.aspectRatio')}
                    aria-valuetext={`${selectedAspectRatioName} ${displayAspectRatio.ratio}`}
                    className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
